import {memset} from '../../lib/utility/helpers';

// ________________________________________________________________________________________________
// imageproc.js
//    This file contains the constructor for a viewport and its associated tile management methods.
//
//    history
//    _______________________________________end_AIHv1/PR __________________________________________
//
// -------------------------------------------------------------------------------------------------
//    BLOB ANALYSIS OBJECT
// -------------------------------------------------------------------------------------------------
//
// suggested improvements:
//        - rename 'l' to be 'label' !
//
export default function BlobAnalysisService (uScope) {
  // ............................................ detect public
  // This function performs connected-component labeling (aka blob extraction)
  // Using Algorithm developed in "A linear-time component labeling algorithm using contour tracing technique"
  this.detect = function (data, w, h) {
    var max = w * h;
    var BACKGROUND = 255;
    var UNSET = 0;
    var MARKED = -1;

    /*
         * 5 6 7
         * 4 P 0
         * 3 2 1
         */
    var pos = [1, w + 1, w, w - 1, -1, -w - 1, -w, -w + 1]; // Clockwise

    var label = []; // Same size as data
    var c = 1; // Component index

    // We change the border to be white. We could add a pixel around
    // but we are lazy and want to do this in place.
    // Set the outer rows/cols to min
    memset(data, 0, w, BACKGROUND); // Top
    memset(data, w * (h - 1), w, BACKGROUND); // Bottom

    for (var y = 1; y < h - 1; y++) {
      var offset = y * w;
      data[offset] = BACKGROUND; // Left
      data[offset + w - 1] = BACKGROUND; // Right
    }

    memset(label, 0, max, UNSET); // Set labels to zeros

    var tracer = function (S, p) {
      for (var d = 0; d < 8; d++) {
        var q = (p + d) % 8;

        var T = S + pos[q];
        if (T < 0 || T >= max) { // Make sure we are inside image
          continue;
        }

        if (data[T] != BACKGROUND) { return {T: T, q: q}; }

        // assert(label[T] <= UNSET);
        label[T] = MARKED;
      }
      return {T: S, q: -1}; // No move
    };

    /**
         *
         * @param S Offset of starting point
         * @param C label count
         * @param external Boolean Is this internal or external tracing
         */
    var contourTracing = function (S, C, external) {
      var p = external ? 7 : 3;

      var tmp = tracer(S, p); // Find out our default next pos (from S)
      var T2 = tmp.T;
      var q = tmp.q;

      label[S] = C;

      if (T2 == S) { // Single pixel check
        return;
      }

      var Tnext = T2;
      var T = T2;

      while (T != S || Tnext != T2) {
        label[Tnext] = C;

        T = Tnext;
        p = (q + 5) % 8;

        tmp = tracer(T, p);
        Tnext = tmp.T;
        q = tmp.q;
      }
    };

    var extract = function () {
      var y = 1; // We start at 1 to avoid looking above the image
      do {
        var x = 0;
        do {
          var offset = y * w + x;
          if (data[offset] == BACKGROUND) { // We skip white pixels or previous labeled pixels
            continue;
          }

          // Step 1 - P not labelled, and above pixel is white
          if (data[offset - w] == BACKGROUND && label[offset] == UNSET) {
            contourTracing(offset, c, true); // P must be external contour
            c++;
          }

          // Step 2 - Below pixel is white, and unmarked
          if (data[offset + w] == BACKGROUND && label[offset + w] == UNSET) {
            var n = label[offset - 1]; // Use previous pixel label, unless this is already labelled
            if (label[offset] != UNSET) { n = label[offset]; }

            // assert(n > UNSET, "Step 2: N must be set, (" + x + "," + y + ") " + n + " " + data[offset - 1]);
            contourTracing(offset, n, false); // P must be a internal contour
          }

          // Step 3 - Not dealt with in previous two steps
          if (label[offset] == UNSET) {
            n = label[offset - 1];
            label[offset] = n; // Assign P the value of N
          }
        } while (x++ < w);
      } while (y++ < (h - 1)); // We end one before the end to to avoid looking below the image

      return label;
    };
    return extract();
  };

  // ............................................ getBlobs public
  // This function returns an array of each blob's bounds
  this.getBlobs = function (labelMask1D, width, height) {
    var blob = [];
    var offset = 0;
    for (var y = 0; y < height; y++) {
      for (var x = 0; x < width; x++) {
        var l = labelMask1D[offset++]; // get this pixel's label

        if (l <= 0) { continue; }

        if (l in blob) { // if already have this blob label in the blob array
          var b = blob[l];

          if (b.x2 < x) { // update bounding box coords: x1 and x2,y2
            b.x2 = x;
          }
          if (b.x1 > x) { b.x1 = x; }
          b.y2 = y; // as we are going from top down, the bottom y should increase
        } else { // add new blob to the blob array
          blob[l] = {valid: true, l: l, x1: x, y1: y, x2: x, y2: y};
        }
      }
    }

    blob[0] = {
      valid: true,
      l: 0,
      x1: 0,
      y1: 0,
      x2: width - 1,
      y2: height - 1,
      width: width,
      height: height,
      area: width * height
    };

    for (var i = 1; i < blob.length; i++) {
      b = blob[i];
      b.width = b.x2 - b.x1 + 1;
      b.height = b.y2 - b.y1 + 1;
      b.area = b.width * b.height;
    }
    return blob;
  };

  // ............................................ addBlobEdges
  // This function builds an array of the blob edges for each valid blob.
  //    Note that the edges are not always connected.  They are not a trace but just a list.
  //    Note that the edges of blob[0] are not computed as it identifies the whole image bounds.
  // todo: probably because of the 1 pixel boundary in detect(),
  //        the blob edges appear to be off one pixel left and top.  So this fn adds one to each edge point.
  this.addBlobEdges = function (blobArray, labelMask1D) {
    if (blobArray.length == 0) {
      return;
    }
    var widthImg = blobArray[0].width;

    for (var i = 1; i < blobArray.length; i++) {
      var blob = blobArray[i];
      if (blob.valid == false) { continue; }
      var thisLabel = blob.l;
      var edges = [];

      var left = blob.x1;
      var top = blob.y1; // blob bounding box
      var width = blob.width;
      var height = blob.height;

      // get left and right boundarys ................
      var offset = 0;
      var x;
      for (var y = top; y < (top + height); y++) {
        offset = y * widthImg + left; // row offset left side
        for (x = left; x < (left + width); x++, offset++) {
          if (labelMask1D[offset] <= 0) { // this pixel does not belong to this blob
            continue;
          }
          if (labelMask1D[offset] == thisLabel) {
            edges.push({x: x + 1, y: y + 1}); // add the left boundary coord
            //    edges.push({ x: x, y: y });         // add the left boundary coord
            break;
          }
        }

        offset = y * widthImg + left + width - 1; // row offset right side
        for (x = (left + width - 1); x >= left; x--, offset--) {
          if (labelMask1D[offset] <= 0) { // this pixel does not belong to this blob
            continue;
          }
          if (labelMask1D[offset] == thisLabel) {
            edges.push({x: x + 1, y: y + 1}); // add the right boundary coord
            //    edges.push({ x: x, y: y });         // add the right boundary coord
            break;
          }
        }
      }

      // get top and bottom boundarys ................
      for (x = left; x < (left + width); x++, offset++) {
        offset = x + top * widthImg; // col offset top side
        for (y = top; y < (top + height); y++, offset += widthImg) {
          if (labelMask1D[offset] <= 0) { // this pixel does not belong to this blob
            continue;
          }
          if (labelMask1D[offset] == thisLabel) {
            edges.push({x: x + 1, y: y + 1}); // add the top boundary coord
            //    edges.push({ x: x, y: y });         // add the top boundary coord
            break;
          }
        }
      }
      for (x = left; x < (left + width); x++, offset++) {
        offset = (top + height) * widthImg + x;
        for (y = (top + height - 1); y >= top; y--, offset -= widthImg) {
          if (labelMask1D[offset] <= 0) { // this pixel does not belong to this blob
            continue;
          }
          if (labelMask1D[offset] == thisLabel) {
            edges.push({x: x + 1, y: y + 1}); // add the bottom boundary coord
            //    edges.push({ x: x, y: y });         // add the bottom boundary coord

            break;
          }
        }
      }
      blob.edges = edges; // add edge list to blob
    }
  };

  // ............................................ addBlobContours
  // This function traces the blob contours and adds them to the blob array
  //
  //    http://en.wikipedia.org/wiki/Moore_neighborhood
  //  The following is a formal description of the Moore-Neighbor tracing algorithm:
  //        Input: A square tessellation, T, containing a connected component P of black cells.
  //        Output: A sequence B (b1, b2, ..., bk) of boundary pixels i.e. the contour.
  //        Define M(a) to be the Moore neighborhood of pixel a. Let p denote the current boundary pixel.
  //        Let c denote the current pixel under consideration i.e. c is in M(p).
  //        Let b denote the backtrack of c (i.e. neighbor pixel of p that was previously tested)
  //
  //        The original termination condition was to stop after visiting the start pixel for the second time.
  //        This limits the set of contours the algorithm will walk completely. An improved stopping condition
  //        proposed by Jacob Eliosoff is to stop after entering the start pixel for the second time in the same
  //        direction you originally entered it.
  //  Begin
  //  Set B to be empty.
  //  From bottom to top and left to right scan the cells of T until a black pixel, s, of P is found.
  //  Insert s in B.
  //  Set the current boundary point p to s i.e. p=s
  //  b = the pixel from which s was entered during the image scan.
  //  Set c to be the next clockwise pixel (from b) in M(p).
  //  While c not equal to s do
  //    If c is black
  //      insert c in B
  //      b = p
  //      p = c
  //      (backtrack: move the current pixel c to the pixel from which p was entered)
  //      c = next clockwise pixel (from b) in M(p).
  //    else
  //      (advance the current pixel c to the next clockwise pixel in M(p) and update backtrack)
  //      b = c
  //      c = next clockwise pixel (from b) in M(p).
  //    end While
  //  End
  // todo: not implemented, not tested yet
  //    this.addBlobContours = function (blob, labelImg2D, width, height) {
  //        for (var i = 0; i < blob.length; i++) {
  //            blob[i].outline = undefined;
  //            var b = blob[i];
  //            if (b.valid == false)
  //                continue;
  //
  //            var label = b.l;
  //            var labelFound = false;
  //            var y0 = b.y1, x0 = b.x1;
  //            do {                                            // find a starting edge pixel for this blob
  //                if (labelImg2D[x0][y0] == label) {
  //                    labelFound = true;
  //                    break;
  //                }
  //                if (++x0 > b.x2) {    // step x, end of row?
  //                    x0 = b.x1;         // step to the begining of the next row
  //                    y0++;
  //                }
  //            } while (y0 < b.y2);
  //            if (labelFound == false)    // this should not happen
  //                continue;
  //
  //            var outline = []; // trace the blob
  //            outline.push({ x: x0, y: y0 }); // starting point
  //            var count = 0, maxCount = b.width * b.height; // safety measures in case loop goes mad
  //            var xc = x0, yc = y0; //current pixel coords
  //            do {
  //                if (yc > 0 && labelImg2D[xc][yc - 1] == label) { // look one pixel above
  //                    yc = yc - 1;
  //                    outline.push({ x: xc, y: yc }); // starting point
  //                }
  //                else if () { // look to the right
  //                }
  //
  //            } while (count < maxCount);
  //
  //        }
  //    };

  // ............................................ color
  // This function draws a picture with each blob coloured
  this.color = function (dest, width, height, labels) {
    // TODO: rect is undefined and it's not clear what the original was supposed to be pointing to
    // TODO: Confirm uScope.rootRectGet() is what the original was expecting
    var rect = uScope.rootRectGet();
    var max = rect.width * rect.height;
    var colors = [];

    var maxcolors = Array.max(labels);
    var maxcolors2 = maxcolors / 2;

    // Create a simple color scale (I could do this in two loops but I'm lazy)
    for (var i = 0; i <= maxcolors; i++) {
      var r = i <= maxcolors2 ? 1 - (i / maxcolors2) : 0;
      var g = i <= maxcolors2 ? i / maxcolors2 : 1 - ((i - maxcolors2) / maxcolors2);
      var b = i <= maxcolors2 ? 0 : ((i - maxcolors2) / maxcolors2);

      colors[i] = [r * 255, g * 255, b * 255];
    }

    var offset = max - 1;
    var destOffset = offset * 4;
    do {
      var l = labels[offset];

      var color = l > 0 ? colors[l] : [0, 0, 0];
      dest[destOffset] = color[0];
      dest[destOffset + 1] = color[1];
      dest[destOffset + 2] = color[2];
      dest[destOffset + 3] = 0xff; // Alpha

      destOffset -= 4;
    } while (offset--);
  };
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/raster-scan/blob-analysis-service.js